import React, { useState, useRef, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import debounce from 'lodash.debounce';
import { CellWrapper, CellContent, CellWithoutDataContent, EllipsisText, StyledInput } from '../styles';

const InputContainer = styled.div`
  position: relative;
  width: 100%;
`;

const DropdownContainer = styled.div`
  position: absolute;
  top: calc(100% + 2px);
  left: 0;
  right: 0;
  z-index: 1000;
  background: white;
  border: 1px solid #bdbdbd;
  border-radius: 4px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  max-height: 200px;
  overflow-y: auto;
  display: ${(props) => (props.show ? 'block' : 'none')};
`;

const OptionItem = styled.div`
  padding: 0.5rem 0.75rem;
  cursor: pointer;
  font-size: 1rem;
  font-family: inherit;
  font-weight: 400;
  color: var(--color-secondary-text);
  border-bottom: 1px solid #f3f4f6;
  transition: background-color 0.2s;

  &:hover {
    background-color: #f8fafc;
  }

  &:last-child {
    border-bottom: none;
  }

  ${(props) => props.highlighted && `
    background-color: #e2e8f0;
  `}
`;

const LoadingItem = styled.div`
  padding: 0.5rem 0.75rem;
  font-size: 1rem;
  font-family: inherit;
  color: #888;
  text-align: center;
`;

const TooltipMessage = styled.div`
  position: absolute;
  top: 50%;
  left: calc(100% + 0.5rem);
  transform: translateY(-50%);
  z-index: 1001;
  background: #333;
  color: white;
  border-radius: 4px;
  padding: 0.5rem 0.75rem;
  font-size: 0.75rem;
  white-space: nowrap;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  opacity: ${(props) => (props.show ? 1 : 0)};
  visibility: ${(props) => (props.show ? 'visible' : 'hidden')};
  transition: opacity 0.2s, visibility 0.2s;
  pointer-events: none;

  &::after {
    content: '';
    position: absolute;
    top: 50%;
    right: 100%;
    transform: translateY(-50%);
    width: 0;
    height: 0;
    border-top: 5px solid transparent;
    border-bottom: 5px solid transparent;
    border-right: 5px solid #333;
  }
`;

const elementContains = (container, element) => {
  if (!container || !element) return false;
  if (container.contains) {
    return container.contains(element);
  }

  let node = element.parentNode;
  while (node) {
    if (node === container) return true;
    node = node.parentNode;
  }

  return false;
};

function AddressInput({
  value: propValue = '',
  editable: propEditable = false,
  onChange,
  left,
  isMissing,
  isDuplicate = false,
  isSelected = false,
  isActive = false,
  isEditing = false,
  onStartEditing,
  onStopEditing,
  rowIndex,
  colIndex,
  registerCellRef,
}) {
  const [value, setValue] = useState(propValue);
  const [initialValue, setInitialValue] = useState(propValue);
  const [editable, setEditable] = useState(propEditable || isEditing);
  const [options, setOptions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [isLoading, setIsLoading] = useState(false);

  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const cellRef = useRef(null);
  const isSavingRef = useRef(false);

  useEffect(() => {
    if (registerCellRef && typeof rowIndex === 'number' && typeof colIndex === 'number') {
      registerCellRef(rowIndex, colIndex, cellRef.current);

      return () => registerCellRef(rowIndex, colIndex, null);
    }

    return undefined;
  }, [registerCellRef, rowIndex, colIndex]);

  const isCurrentlyEditing = isEditing !== undefined ? isEditing : editable;

  const geocodeAddress = useCallback((query) => {
    if (window.google && window.google.maps) {
      const geocoder = new window.google.maps.Geocoder();

      geocoder.geocode(
        {
          address: query,
          componentRestrictions: {
            country: 'US',
          },
        },
        (results, status) => {
          if (status === 'OK' && results && results.length > 0) {
            const mappedOptions = results.map((result) => ({
              label: result.formatted_address,
              value: result.formatted_address,
              result,
            }));
            setOptions(mappedOptions);
          } else {
            setOptions([]);
          }

          setIsLoading(false);
        },
      );
    } else {
      setOptions([]);
      setIsLoading(false);
    }
  }, []);

  const debouncedGeocode = useCallback(
    debounce((query) => {
      geocodeAddress(query);
    }, 400, { leading: false, trailing: true }),
    [geocodeAddress],
  );

  useEffect(() => {
    if (propValue !== initialValue && !propEditable && isEditing === undefined) {
      setValue(propValue);
      setInitialValue(propValue);
      setEditable(false);
    } else if (propValue !== initialValue && isEditing === undefined) {
      setValue(propValue);
      setInitialValue(propValue);
    }
  }, [propValue, propEditable, initialValue, isEditing, editable]);

  useEffect(() => {
    if (isEditing !== undefined) {
      setEditable(isEditing);
      if (isEditing && inputRef.current) {
        setTimeout(() => {
          const inputElement = inputRef.current;
          if (inputElement && inputElement.focus && typeof inputElement.focus === 'function') {
            inputElement.focus();
            if (inputElement.select && typeof inputElement.select === 'function') {
              inputElement.select();
            }
          }
        }, 0);
      } else {
        setShowDropdown(false);
        setHighlightedIndex(-1);
        setIsLoading(false);
      }
    }
  }, [isEditing]);


  const handleChange = (e) => {
    const newValue = e.target.value;
    setValue(newValue);

    if (newValue.length >= 3) {
      setIsLoading(true);
      setShowDropdown(true);
      setOptions([]);
      setHighlightedIndex(-1);

      debouncedGeocode(newValue);
    } else {
      setShowDropdown(false);
      setOptions([]);
      setIsLoading(false);
    }
  };

  const handleBlur = useCallback((e) => {
    if (isSavingRef.current) return;

    const currentValue = e.target ? e.target.value : value;

    setTimeout(() => {
      const currentContainer = containerRef.current;
      const activeElement = document.activeElement;

      if (!currentContainer || !activeElement || !elementContains(currentContainer, activeElement)) {
        setInitialValue(currentValue);

        if (isEditing !== undefined && onStopEditing) {
          onStopEditing();
        } else {
          setEditable(false);
        }

        setShowDropdown(false);
        setHighlightedIndex(-1);
        setIsLoading(false);

        if (onChange && currentValue !== initialValue) {
          onChange(currentValue);
        }
      }
    }, 150);
  }, [value, isEditing, onStopEditing, onChange, initialValue]);

  const handleKeyDown = (e) => {
    if (showDropdown && options.length > 0) {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setHighlightedIndex((prev) => (prev < options.length - 1 ? prev + 1 : 0));

          return;
        case 'ArrowUp':
          e.preventDefault();
          setHighlightedIndex((prev) =>
            (prev > 0 ? prev - 1 : options.length - 1));

          return;
        case 'Enter':
          e.preventDefault();
          if (highlightedIndex >= 0) {
            selectOption(options[highlightedIndex]);
          } else {
            handleEnterSave();
          }

          return;
        case 'Escape':
          e.preventDefault();
          handleEscapeCancel();

          return;
        default:
          break;
      }
    }

    if (e.key === 'Enter') {
      e.preventDefault();
      if (isCurrentlyEditing) {
        handleEnterSave();
      } else {
        handleEnterEdit();
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleEscapeCancel();
    }
  };

  const handleEnterSave = useCallback(() => {
    isSavingRef.current = true;

    setInitialValue(value);
    if (isEditing !== undefined && onStopEditing) {
      onStopEditing();
    } else {
      setEditable(false);
    }

    setShowDropdown(false);
    setIsLoading(false);
    if (onChange) {
      onChange(value);
    }

    setTimeout(() => {
      isSavingRef.current = false;
    }, 300);
  }, [value, isEditing, onStopEditing, onChange]);

  const handleEscapeCancel = useCallback(() => {
    isSavingRef.current = false;
    setValue(initialValue);
    setShowDropdown(false);
    setHighlightedIndex(-1);
    setIsLoading(false);
    if (isEditing !== undefined && onStopEditing) {
      onStopEditing();
    } else {
      setEditable(false);
    }
  }, [initialValue, isEditing, onStopEditing]);

  const handleEnterEdit = useCallback(() => {
    if (!isCurrentlyEditing) {
      if (isEditing !== undefined && onStartEditing) {
        onStartEditing();
      } else {
        setEditable(true);
      }
    }
  }, [isCurrentlyEditing, isEditing, onStartEditing]);

  const edit = useCallback(() => {
    if (isEditing !== undefined && onStartEditing) {
      onStartEditing();
    } else {
      setEditable(true);
    }
  }, [isEditing, onStartEditing]);

  const selectOption = (option) => {
    isSavingRef.current = true;
    setValue(option.value);
    setInitialValue(option.value);
    setShowDropdown(false);
    setHighlightedIndex(-1);
    setIsLoading(false);

    if (isEditing !== undefined && onStopEditing) {
      onStopEditing();
    } else {
      setEditable(false);
    }

    if (onChange) {
      onChange(option.value);
    }

    setTimeout(() => {
      isSavingRef.current = false;
    }, 300);
  };

  const handleCellKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (isCurrentlyEditing) {
        handleEnterSave();
      } else {
        handleEnterEdit();
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleEscapeCancel();
    }
  };

  return (
    <CellWrapper
      ref={cellRef}
      isActive={isActive}
      isEditing={isCurrentlyEditing}
      onKeyDown={!isCurrentlyEditing ? handleCellKeyDown : undefined}
      tabIndex={!isCurrentlyEditing ? 0 : -1}
    >
      <InputContainer ref={containerRef}>
        {isCurrentlyEditing ? (
          <CellContent>
            <StyledInput
              ref={inputRef}
              value={value}
              onChange={handleChange}
              onBlur={handleBlur}
              onKeyDown={handleKeyDown}
              autoFocus
              autoComplete="off"
              placeholder="Enter address..."
              isDuplicate={isDuplicate}
              aria-invalid={isDuplicate}
            />

            <DropdownContainer show={showDropdown}>
              {isLoading && (
                <LoadingItem>Searching addresses...</LoadingItem>
              )}
              {!isLoading && options.length > 0 && (
                options.map((option, index) => (
                  <OptionItem
                    key={option.value}
                    highlighted={index === highlightedIndex}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => selectOption(option)}
                    onMouseEnter={() => setHighlightedIndex(index)}
                  >
                    {option.label}
                  </OptionItem>
                ))
              )}
              {!isLoading && options.length === 0 && value.length >= 3 && (
                <LoadingItem>No addresses found</LoadingItem>
              )}
            </DropdownContainer>
          </CellContent>
        ) : (
          <>
            {value ? (
              <EllipsisText
                left={left}
                onClick={edit}
                isSelected={isSelected}
                isActive={isActive}
                isDuplicate={isDuplicate}
              >
                {value}
              </EllipsisText>
            ) : (
              <CellWithoutDataContent
                left={left}
                onClick={edit}
                isMissing={isMissing}
                isActive={isActive}
              />
            )}
          </>
        )}

        {isDuplicate && (
          <TooltipMessage show={!isCurrentlyEditing}>
            This address has already been added
          </TooltipMessage>
        )}
      </InputContainer>
    </CellWrapper>
  );
}

AddressInput.propTypes = {
  value: PropTypes.string,
  editable: PropTypes.bool,
  onChange: PropTypes.func,
  left: PropTypes.bool,
  isMissing: PropTypes.bool,
  isDuplicate: PropTypes.bool,
  isSelected: PropTypes.bool,
  isActive: PropTypes.bool,
  isEditing: PropTypes.bool,
  onStartEditing: PropTypes.func,
  onStopEditing: PropTypes.func,
  rowIndex: PropTypes.number,
  colIndex: PropTypes.number,
  registerCellRef: PropTypes.func,
};

AddressInput.defaultProps = {
  value: '',
  editable: false,
  onChange: null,
  left: false,
  isMissing: false,
  isDuplicate: false,
  isSelected: false,
  isActive: false,
  isEditing: undefined,
  onStartEditing: null,
  onStopEditing: null,
  rowIndex: undefined,
  colIndex: undefined,
  registerCellRef: null,
};

export default AddressInput;
